import { FormikConfig, FormikHelpers } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { FormikInput } from '../../../components/formik/FormInput';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { T, useT } from '../../../translation/src';
import { OrganisationCreateMutation, useOrganisationCreateMutation } from '../../../types/graphqlTypes';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

export const useValidationSchema = () => {
  const translations = useValidationTranslations();

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().trim().required(translations.isRequired),
      }),
    [translations],
  );

  return schema;
};

interface FormValues {
  name: string;
}

const useTranslations = () => {
  const name = useT('name', { swc: true });
  return { name };
};
export interface AddOrganisationModalProps {
  onClose: (args?: { isSuccess: boolean; data?: OrganisationCreateMutation['organisationCreate'] }) => void;
  open?: boolean;
}
const AddOrganisationModal_: React.FC2<AddOrganisationModalProps> = ({ onClose, open }) => {
  const validationSchema = useValidationSchema();
  const translations = useTranslations();
  const [addOrganisation, { loading: addOrganisationLoading }] = useOrganisationCreateMutation();
  const validationSchemaTranslations = useValidationTranslations();
  const onSuccess = useCallback(
    ({ helpers: { resetForm }, data }: { helpers: FormikHelpers<FormValues>; data?: OrganisationCreateMutation['organisationCreate'] }) => {
      onClose({ isSuccess: true, data });
      // Because of transition
      setTimeout(() => {
        resetForm();
      }, 250);
    },
    [onClose],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      try {
        const { data } = await addOrganisation({
          variables: { name: values.name.trim() },
          refetchQueries: ['organisations', 'currentUser'],
          awaitRefetchQueries: true,
        });

        onSuccess({ helpers, data: data?.organisationCreate });
      } catch (error) {
        if (CombinedGraphQLErrors.is(error)) {
          if (error.message === 'ALREADY_EXISTS') {
            helpers.setFieldError('name', validationSchemaTranslations.alreadyExists);
          }
        } else {
          console.error(error);
          throw error;
        }
      }
    },
    [addOrganisation, onSuccess, validationSchemaTranslations.alreadyExists],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: { name: '' },
      onSubmit,
      validationSchema,
    }),
    [onSubmit, validationSchema],
  );

  return (
    <CreateModal title={<T _str="add organisation" swc />} createButtonTitle={<T _str="create" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={addOrganisationLoading} noOverflow>
      <FormikInput name="name" label={translations.name} placeholder={translations.name} />
    </CreateModal>
  );
};

export const AddOrganisationModal = memo(AddOrganisationModal_);
